import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { Currency } from '@tradecafe/types/core'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { BehaviorSubject, merge } from 'rxjs'
import { distinctUntilChanged, mapTo } from 'rxjs/operators'
import { cloneShipmentRates, cloneShipmentRatesFailure, cloneShipmentRatesSuccess } from 'src/app/store/shipment-rates'
import { dayjs } from 'src/services/dayjs'


export interface CopyFreightRatesFormOptions {
  rateIds: string[]
  currency: Currency
}

@Component({
  selector: 'tc-copy-freight-rates-form',
  templateUrl: './copy-freight-rates-form.component.html',
  styleUrls: ['./copy-freight-rates-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CopyFreightRatesFormComponent extends OnDestroyMixin implements OnInit {
  private readonly actions$ = inject(Actions)
  private readonly store = inject(Store)
  private readonly dialogRef = inject<MatDialogRef<CopyFreightRatesFormComponent>>(MatDialogRef)
  private readonly dialogData = inject<CopyFreightRatesFormOptions>(MAT_DIALOG_DATA)


  protected readonly currencyCode = this.dialogData.currency.code

  protected readonly inProgress$ = new BehaviorSubject<'saving'>(undefined);

  protected readonly batchChange = new FormGroup({
    sign: new FormControl(-1, Validators.required),
    amount: new FormControl(0, Validators.required),
    dateQuoted: new FormControl(dayjs().unix(), Validators.required),
    validUntil: new FormControl(dayjs().add(1, 'y').unix(), Validators.required),
    retireNow: new FormControl(false),
  })

  ngOnInit(): void {
    merge(
      this.actions$.pipe(ofType(cloneShipmentRates), mapTo('saving' as const)),
      this.actions$.pipe(ofType(cloneShipmentRatesFailure), mapTo(undefined)),
    ).pipe(distinctUntilChanged(), untilComponentDestroyed(this)).subscribe(inProgress => this.inProgress$.next(inProgress))
    this.actions$.pipe(ofType(cloneShipmentRatesSuccess), untilComponentDestroyed(this)).subscribe(action => {
      this.dialogRef.close(action.rates)
    })
  }

  protected async submit() {
    this.batchChange.markAllAsTouched()
    if (!this.batchChange.valid) return
    const form = this.batchChange.value

    this.store.dispatch(cloneShipmentRates({
      payload: {
        rate_ids: this.dialogData.rateIds,
        change: form.sign * form.amount,
        date_quoted: form.dateQuoted,
        until: form.validUntil,
        retire_now: form.retireNow,
      }
    }))
  }
}
